import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { useMutation } from '@tanstack/react-query';
import {
	getOnboardingScore,
	GetOnboardingScoringResponse
} from '@/services/onboardingLeadScore';
import { HttpResponseSuccess } from '@/services/service';
import { UserRole } from '@/modules/user/userTypes';

export function useOnboardingScore() {
	const [score, setScore] = useState<number | null>(null);
	const hasFetchedRef = useRef(false);

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboarding = user?.onboarding?.status === 'pending';
	const isAllowedRole = user?.role === UserRole.PreTrial && isUserOnboarding;

	const { mutate: fetchScore, isLoading } = useMutation<
		HttpResponseSuccess<GetOnboardingScoringResponse>,
		Error
	>({
		mutationFn: getOnboardingScore,
		onSuccess: res => {
			const s = res?.data?.data?.scoring?.score;
			if (typeof s === 'number') {
				setScore(s);
			}
		},
		onError: err => {
			console.error('Error fetching onboarding score:', err);
		}
	});

	useEffect(() => {
		if (hasFetchedRef.current || !isAllowedRole) return;

		hasFetchedRef.current = true;
		fetchScore();
	}, [fetchScore, isAllowedRole]);

	return { score, loading: isLoading };
}
